import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type AppearanceMode = 'light' | 'dark';

interface AppearanceContextValue {
  mode: AppearanceMode;
  toggleAppearance: () => void;
}

const STORAGE_KEY = 'fintitles-appearance';
const AppearanceContext = createContext<AppearanceContextValue | null>(null);

const getStoredMode = (): AppearanceMode | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
};

const getInitialMode = (): AppearanceMode => 'dark';

export function AppearanceProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { pathname } = useLocation();
  const [storedMode, setStoredMode] = useState<AppearanceMode | null>(getStoredMode);
  const [systemMode, setSystemMode] = useState<AppearanceMode>(getInitialMode);
  const isPublicPage = pathname === '/' || /^\/login\/?$/.test(pathname);
  const mode = isPublicPage ? 'dark' : storedMode ?? systemMode;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemMode = (event: MediaQueryListEvent): void => setSystemMode(event.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', updateSystemMode);
    return () => mediaQuery.removeEventListener('change', updateSystemMode);
  }, []);

  useEffect(() => {
    const syncMode = (event: StorageEvent): void => {
      if (event.key === STORAGE_KEY) setStoredMode(event.newValue === 'light' || event.newValue === 'dark' ? event.newValue : null);
    };
    window.addEventListener('storage', syncMode);
    return () => window.removeEventListener('storage', syncMode);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', mode === 'dark' ? '#1f1f1f' : '#ffffff');
  }, [mode]);

  const value = useMemo<AppearanceContextValue>(() => ({
    mode,
    toggleAppearance: () => {
      const nextMode = mode === 'dark' ? 'light' : 'dark';
      setStoredMode(nextMode);
      try { window.localStorage.setItem(STORAGE_KEY, nextMode); } catch { /* Use this tab's selection when storage is unavailable. */ }
    },
  }), [mode]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppAppearance(): AppearanceContextValue {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error('useAppAppearance must be used within AppearanceProvider');
  return context;
}
